import type { InventoryActivity } from "../stores/telemetryStore";

/**
 * ADR 363 — the live count's number. The overlay (ADR 324) paints which addresses a
 * sweep has reached; this turns the same visited set into "4.812 de 15.538 (31 %)",
 * a pace, and a finish estimate. Still addresses only — never a product, never a
 * quantity (seam 16).
 *
 * The pace is read from first visits that arrived in LIVE deltas inside a trailing
 * window. The on-subscribe snapshot replays history with one timestamp, so it is
 * excluded — otherwise a tab opened mid-sweep would read a 10 000-address burst as a
 * pace and estimate the finish for "now".
 */
export interface LiveCountStats {
  visited: number;
  /** Registered addresses of the warehouse; null when the structure is unknown. */
  total: number | null;
  pct: number | null;
  /** Addresses per hour over the trailing window; null until the window is wide enough. */
  ratePerHour: number | null;
  /** Estimated finish, epoch ms; null without a rate or a total. */
  etaMs: number | null;
  /** Ms since the sweep's first observation; null when the start is unknown. */
  elapsedMs: number | null;
  /** Ms since the last observation reached this tab. */
  silentMs: number;
}

export const PACE_WINDOW_MS = 10 * 60_000;
/** A rate needs at least this much live window behind it before it is quoted. */
export const PACE_MIN_SPAN_MS = 2 * 60_000;
/** A sweep silent this long is not "live" any more, whatever the store still holds. */
export const LIVE_STALE_MS = 30 * 60_000;

export function liveCountStats(
  a: InventoryActivity, total: number | null, nowMs: number,
): LiveCountStats {
  const visited = a.visited.size;
  const pct = total && total > 0 ? Math.min(100, (100 * visited) / total) : null;
  const elapsedMs = a.firstTsMs === null ? null : Math.max(0, nowMs - a.firstTsMs);
  const silentMs = Math.max(0, nowMs - a.lastTsMs);

  let ratePerHour: number | null = null;
  if (a.liveSinceMs !== null) {
    const windowStart = Math.max(nowMs - PACE_WINDOW_MS, a.liveSinceMs);
    const span = nowMs - windowStart;
    if (span >= PACE_MIN_SPAN_MS) {
      let n = 0;
      for (let i = a.visitLog.length - 1; i >= 0 && a.visitLog[i] >= windowStart; i--) n++;
      ratePerHour = (n * 3_600_000) / span;
    }
  }
  let etaMs: number | null = null;
  if (ratePerHour !== null && ratePerHour > 0 && total !== null && total > visited) {
    etaMs = nowMs + ((total - visited) * 3_600_000) / ratePerHour;
  }
  return { visited, total, pct, ratePerHour, etaMs, elapsedMs, silentMs };
}

/** Is this activity the sweep of the tab's active site/warehouse, and still fresh? */
export function activityMatches(
  a: InventoryActivity | null, siteId: string | undefined, warehouseId: string | undefined, nowMs: number,
): a is InventoryActivity {
  if (!a || !siteId || a.siteId !== siteId) return false;
  if (a.warehouseId && warehouseId && a.warehouseId !== warehouseId) return false;
  return nowMs - a.lastTsMs <= LIVE_STALE_MS;
}
